import React, {
  Context,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createCurrencyConfig,
  DEFAULT_STAKE_SETTINGS,
  getStakeConfig,
} from "../services/gameConfigService";
import type {
  Bet,
  SessionBet,
  SocketContextType,
  SocketEventType,
} from "../providers/SocketProvider";
import { DEMO_CONFIG } from "./demo.config";
import { demoGameService } from "./demo-game.service";
import type {
  DemoAction,
  DemoBetPayload,
  DemoSocketMessage,
} from "./demo-round-engine.service";
import { createDemoPlayer } from "./demo-player.model";

type SubscriberMap = Map<SocketEventType, Set<(data: unknown) => void>>;

interface DemoSocketProviderProps {
  children: React.ReactNode;
  context: Context<SocketContextType | undefined>;
}

const createSeed = () => {
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    bytes.forEach((_, index) => {
      bytes[index] = Math.floor(Math.random() * 256);
    });
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

const asGameStatus = (status: "WAIT" | "RUN" | "BLAST") =>
  status as SocketContextType["gameData"]["status"];

const toSocketBet = (bet: DemoBetPayload): Bet => ({
  betId: bet.betId,
  userName: bet.clientName,
  avatar: "/avatars/av-1.png",
  stake: String(bet.stake),
  oddsRate: bet.oddsRate || 1,
  cashOut: bet.cashOut || 0,
  nation: bet.nation as Bet["nation"],
  status: bet.nation as Bet["status"],
  roundId: String(bet.round),
  betType: bet.betType,
  createdAt: bet.createdAt,
  eventId: "demo-aviator",
});

const DemoSocketProvider = ({
  children,
  context: SocketContext,
}: DemoSocketProviderProps) => {
  const player = useMemo(createDemoPlayer, []);
  const [isConnected, setIsConnected] = useState(false);
  const [balance, setBalance] = useState(player.balance);
  const [marketData, setMarketData] = useState<DemoSocketMessage | null>(null);
  const [lastBetResult, setLastBetResult] =
    useState<DemoSocketMessage | null>(null);
  const [gameData, setGameData] = useState<SocketContextType["gameData"]>({});
  const [bets, setBets] = useState<Bet[]>([]);
  const [sessionBetHistory, setSessionBetHistory] = useState<SessionBet[]>([]);
  const [roundId, setRoundId] = useState(demoGameService.roundEngine.getRoundId());
  const [clientSeed, setClientSeed] = useState(createSeed);
  const subscribersRef = useRef<SubscriberMap>(new Map());

  const currencyConfig = useMemo(() => {
    const stakeConfig = getStakeConfig(
      DEFAULT_STAKE_SETTINGS,
      DEMO_CONFIG.currency,
    );
    return createCurrencyConfig(DEMO_CONFIG.currency, stakeConfig);
  }, []);

  const notify = useCallback((event: SocketEventType, data: unknown) => {
    subscribersRef.current
      .get(event)
      ?.forEach((subscriber) => subscriber(data));
  }, []);

  const handleMessage = useCallback(
    (message: DemoSocketMessage) => {
      setMarketData(message);

      if (message.type === "CONNECTED") {
        setIsConnected(true);
        notify("connection_status", { status: "connected" });
        notify("auth_success", { type: "auth_success", demo: true });
        return;
      }

      if (message.type === "balance_update") {
        if (typeof message.balance === "number") setBalance(message.balance);
        notify("balance_update", message);
        return;
      }

      if (message.p === "WAIT") {
        if (typeof message.r === "number") setRoundId(message.r);
        setGameData({
          status: asGameStatus("WAIT"),
          startsIn: message.tl || 0,
          multiplier: 1,
        });
        notify("game_update", message);
        return;
      }

      if (typeof message.rv === "number") {
        setGameData({
          status: asGameStatus("RUN"),
          startsIn: 0,
          multiplier: message.rv,
        });
        notify("game_update", message);
        return;
      }

      if (message.p === "RUN") {
        setGameData((previous) => ({
          ...previous,
          status: asGameStatus("RUN"),
          startsIn: 0,
          multiplier: previous.multiplier || 1,
        }));
        notify("game_update", message);
        return;
      }

      if (message.p === "CRASH") {
        const crashPoint = message.cv || 1;
        const crashedRound = String(
          message.r || demoGameService.roundEngine.getRoundId(),
        );
        setGameData({
          status: asGameStatus("BLAST"),
          crashPoint,
          multiplier: crashPoint,
        });
        setSessionBetHistory((previous) =>
          previous.map((bet) =>
            bet.status === "placed" && bet.roundId === crashedRound
              ? { ...bet, status: "lost", multiplier: crashPoint }
              : bet,
          ),
        );
        notify("game_update", message);
        notify("bet_result", message);
        return;
      }

      if (message.type === "3" && message.status === "success" && message.bets) {
        const acceptedBets = message.bets.map(toSocketBet);
        setBets((previous) => {
          const next = [...previous];
          acceptedBets.forEach((bet) => {
            const index = next.findIndex((item) => item.betId === bet.betId);
            if (index >= 0) next[index] = bet;
            else next.push(bet);
          });
          return next;
        });
        setSessionBetHistory((previous) => {
          const next = [...previous];
          acceptedBets.forEach((bet) => {
            const roundKey = String(bet.roundId || "");
            const betType = String(bet.betType || "1");
            const index = next.findIndex(
              (item) =>
                item.roundId === roundKey && item.betType === betType,
            );
            const sessionBet: SessionBet = {
              sessionId: `demo-session-${bet.betId}`,
              betId: bet.betId,
              betType,
              roundId: roundKey,
              stake: Number(bet.stake) || 0,
              placedAt: bet.createdAt || new Date().toISOString(),
              status: "placed",
            };
            if (index >= 0) next[index] = sessionBet;
            else next.unshift(sessionBet);
          });
          return next;
        });
        setLastBetResult(message);
        notify("bet_result", message);
        return;
      }

      if (message.type === "7" && message.action === "CancelBet") {
        setBets((previous) =>
          previous.filter((bet) => bet.betId !== message.betId),
        );
        setSessionBetHistory((previous) =>
          previous.filter((bet) => bet.betId !== message.betId),
        );
        setLastBetResult(message);
        notify("bet_result", message);
        return;
      }

      if (message.type === "cashout_success" && message.betId) {
        const payout = Number(message.cashOut || message.cashout || 0);
        const multiplier = Number(message.multiplier || message.RUNValue || 1);
        setBets((previous) =>
          previous.map((bet) =>
            bet.betId === message.betId
              ? {
                  ...bet,
                  nation: "CASHOUT" as Bet["nation"],
                  status: "CASHOUT" as Bet["status"],
                  oddsRate: multiplier,
                  cashOut: payout,
                  isWinner: true,
                }
              : bet,
          ),
        );
        setSessionBetHistory((previous) =>
          previous.map((bet) =>
            bet.betId === message.betId
              ? {
                  ...bet,
                  multiplier,
                  cashout: payout,
                  status: "cashed_out",
                }
              : bet,
          ),
        );
        setLastBetResult(message);
        notify("bet_result", message);
        return;
      }

      if (message.type === "ERROR") {
        setLastBetResult(message);
        notify("bet_result", message);
      }
    },
    [notify],
  );

  useEffect(() => {
    const unsubscribeBalance = demoGameService.wallet.subscribe(setBalance);
    const unsubscribeEngine =
      demoGameService.roundEngine.subscribe(handleMessage);
    demoGameService.start();

    return () => {
      unsubscribeBalance();
      unsubscribeEngine();
      demoGameService.stop();
    };
  }, [handleMessage]);

  const connect = useCallback(() => demoGameService.start(), []);
  const disconnect = useCallback(() => {
    demoGameService.stop();
    setIsConnected(false);
  }, []);
  const sendMessage = useCallback(
    (message: DemoAction | DemoAction[]) => demoGameService.send(message),
    [],
  );
  const subscribe = useCallback(
    (event: SocketEventType, callback: (data: unknown) => void) => {
      const subscribers =
        subscribersRef.current.get(event) ||
        new Set<(data: unknown) => void>();
      subscribers.add(callback);
      subscribersRef.current.set(event, subscribers);
      return () => subscribers.delete(callback);
    },
    [],
  );
  const unsubscribe = useCallback(
    (event: SocketEventType, callback: (data: unknown) => void) => {
      subscribersRef.current.get(event)?.delete(callback);
    },
    [],
  );
  const resetDemoBalance = useCallback(() => demoGameService.resetBalance(), []);

  const contextValue: SocketContextType = {
    isConnected,
    isAuthenticated: true,
    balance,
    currency: player.currency,
    currencyConfig,
    gameConfigReady: true,
    marketData,
    lastBetResult,
    connect,
    disconnect,
    sendMessage,
    subscribe: subscribe as SocketContextType["subscribe"],
    unsubscribe: unsubscribe as SocketContextType["unsubscribe"],
    needToSendPrevious: () => false,
    encryptionState: {
      hasKeyPair: false,
      hasServerKey: false,
      hasEncryptionKey: false,
    },
    getMarketData: () => ({
      subscribe: (callback: (data: unknown) => void) => {
        const stop = demoGameService.roundEngine.subscribe(callback);
        return stop;
      },
    }),
    gameData,
    bets,
    sessionBetHistory,
    userCount: 24,
    eventId: "demo-aviator",
    roundId,
    clientSeed,
    generateNewSeed: () => {
      const seed = createSeed();
      setClientSeed(seed);
      return seed;
    },
    changeClientSeed: (seed: string) => {
      const normalized = seed.trim();
      if (normalized) setClientSeed(normalized);
    },
    serverSeedHash: "",
    refreshConnection: () => {
      demoGameService.stop();
      demoGameService.start();
    },
    getPublicKeyBase64: () => null,
    isEncryptionEnabled: false,
    isDemoMode: true,
    resetDemoBalance,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export default DemoSocketProvider;
